export interface CrmTaskCreateSelectOption {
  value: string;
  label: string;
}

export interface CrmTaskCreateOfferContextSummary {
  offerType: string;
  status: string;
  paymentStatus: string;
}

interface CrmTaskCreateBasicsPanelProps {
  title: string;
  description: string;
  taskType: string;
  taskTypeOptions: readonly CrmTaskCreateSelectOption[];
  assignedTo: string;
  memberOptions: readonly CrmTaskCreateSelectOption[];
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onTaskTypeChange: (value: string) => void;
  onAssignedToChange: (value: string) => void;
}

interface CrmTaskCreateRelatedRecordsPanelProps {
  contactSearch: string;
  personId: string;
  contactOptions: readonly CrmTaskCreateSelectOption[];
  isLoadingContacts: boolean;
  contactsError: boolean;
  dealId: string;
  dealOptions: readonly CrmTaskCreateSelectOption[];
  isLoadingDeals: boolean;
  dealsError: boolean;
  companySearch: string;
  companyId: string;
  companyOptions: readonly CrmTaskCreateSelectOption[];
  isLoadingCompanies: boolean;
  companiesError: boolean;
  offerSearch: string;
  offerId: string;
  offerOptions: readonly CrmTaskCreateSelectOption[];
  isLoadingOffers: boolean;
  selectedOfferContext: CrmTaskCreateOfferContextSummary | null;
  onContactSearchChange: (value: string) => void;
  onPersonIdChange: (value: string) => void;
  onDealIdChange: (value: string) => void;
  onCompanySearchChange: (value: string) => void;
  onCompanyIdChange: (value: string) => void;
  onOfferSearchChange: (value: string) => void;
  onOfferIdChange: (value: string) => void;
}

interface CrmTaskCreateSchedulePanelProps {
  dueAt: string;
  priority: string;
  recurrenceFrequency: string;
  recurrenceInterval: string;
  onDueAtChange: (value: string) => void;
  onPriorityChange: (value: string) => void;
  onRecurrenceFrequencyChange: (value: string) => void;
  onRecurrenceIntervalChange: (value: string) => void;
}

const TASK_PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
] as const;

const TASK_RECURRENCE_OPTIONS = [
  { value: "none", label: "Does not repeat" },
  { value: "daily", label: "Repeats daily" },
  { value: "weekly", label: "Repeats weekly" },
  { value: "biweekly", label: "Repeats every two weeks" },
  { value: "monthly", label: "Repeats monthly" },
] as const;

export function CrmTaskCreateBasicsPanel({
  title,
  description,
  taskType,
  taskTypeOptions,
  assignedTo,
  memberOptions,
  onTitleChange,
  onDescriptionChange,
  onTaskTypeChange,
  onAssignedToChange,
}: CrmTaskCreateBasicsPanelProps) {
  return (
    <>
      <input
        type="text"
        value={title}
        onChange={(event) => onTitleChange(event.target.value)}
        placeholder="Task title *"
        autoFocus
        className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
      />
      <textarea
        value={description}
        onChange={(event) => onDescriptionChange(event.target.value)}
        placeholder="Description (optional)"
        rows={3}
        className="w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
      />
      <label className="block text-sm">
        <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Task type</span>
        <select
          value={taskType}
          onChange={(event) => onTaskTypeChange(event.target.value)}
          className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
        >
          {taskTypeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-sm">
        <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Assigned to</span>
        <select
          value={assignedTo}
          onChange={(event) => onAssignedToChange(event.target.value)}
          aria-label="Assign task to team member"
          className="mt-1 w-full rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
        >
          <option value="">Unassigned</option>
          {memberOptions.map((member) => (
            <option key={member.value} value={member.value}>
              {member.label}
            </option>
          ))}
        </select>
        {memberOptions.length === 0 && (
          <span className="mt-1 block text-xs text-[var(--color-muted-foreground)]">
            Team members are unavailable, so the task will be created unassigned.
          </span>
        )}
      </label>
    </>
  );
}

export function CrmTaskCreateRelatedRecordsPanel({
  contactSearch,
  personId,
  contactOptions,
  isLoadingContacts,
  contactsError,
  dealId,
  dealOptions,
  isLoadingDeals,
  dealsError,
  companySearch,
  companyId,
  companyOptions,
  isLoadingCompanies,
  companiesError,
  offerSearch,
  offerId,
  offerOptions,
  isLoadingOffers,
  selectedOfferContext,
  onContactSearchChange,
  onPersonIdChange,
  onDealIdChange,
  onCompanySearchChange,
  onCompanyIdChange,
  onOfferSearchChange,
  onOfferIdChange,
}: CrmTaskCreateRelatedRecordsPanelProps) {
  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted-foreground)]">Related records</p>
      <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
        Search and link manual work to CRM records so the task appears in the right contact, deal, company, or offer context.
      </p>
      <div className="mt-3 grid gap-3">
        <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
          <input
            type="search"
            value={contactSearch}
            onChange={(event) => onContactSearchChange(event.target.value)}
            placeholder="Search contacts"
            aria-label="Search related contacts"
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
          />
          <select
            value={personId}
            onChange={(event) => onPersonIdChange(event.target.value)}
            aria-label="Related contact"
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
          >
            <option value="">{isLoadingContacts ? "Loading contacts..." : "No related contact"}</option>
            {contactOptions.map((contact) => (
              <option key={contact.value} value={contact.value}>
                {contact.label}
              </option>
            ))}
          </select>
        </div>
        {contactsError && (
          <p className="text-xs text-[var(--color-warning)]">Contact search unavailable. You can still create the task without a contact link.</p>
        )}
        <div className="grid gap-2 sm:grid-cols-2">
          <select
            value={dealId}
            onChange={(event) => onDealIdChange(event.target.value)}
            aria-label="Related deal"
            className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
          >
            <option value="">{isLoadingDeals ? "Loading deals..." : "No related deal"}</option>
            {dealOptions.map((deal) => (
              <option key={deal.value} value={deal.value}>
                {deal.label}
              </option>
            ))}
          </select>
          <div className="grid gap-2">
            <input
              type="search"
              value={companySearch}
              onChange={(event) => onCompanySearchChange(event.target.value)}
              placeholder="Search companies"
              aria-label="Search related companies"
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
            />
            <select
              value={companyId}
              onChange={(event) => onCompanyIdChange(event.target.value)}
              aria-label="Related company"
              className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
            >
              <option value="">{isLoadingCompanies ? "Loading companies..." : "No related company"}</option>
              {companyOptions.map((company) => (
                <option key={company.value} value={company.value}>
                  {company.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {(dealsError || companiesError) && (
          <p className="text-xs text-[var(--color-warning)]">
            Some related-record selectors are temporarily unavailable. The task can still be created and linked later.
          </p>
        )}
        <input
          type="search"
          value={offerSearch}
          onChange={(event) => onOfferSearchChange(event.target.value)}
          placeholder="Search offers"
          aria-label="Search related offers"
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
        />
        <select
          value={offerId}
          onChange={(event) => onOfferIdChange(event.target.value)}
          aria-label="Related offer"
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]/30"
        >
          <option value="">{isLoadingOffers ? "Loading offers..." : "No related offer"}</option>
          {offerOptions.map((offer) => (
            <option key={offer.value} value={offer.value}>
              {offer.label}
            </option>
          ))}
        </select>
        {selectedOfferContext && (
          <p className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-xs text-[var(--color-muted-foreground)]">
            Offer context will be saved with this task: {selectedOfferContext.offerType}, {selectedOfferContext.status}, payment {selectedOfferContext.paymentStatus}.
          </p>
        )}
      </div>
    </div>
  );
}

export function CrmTaskCreateSchedulePanel({
  dueAt,
  priority,
  recurrenceFrequency,
  recurrenceInterval,
  onDueAtChange,
  onPriorityChange,
  onRecurrenceFrequencyChange,
  onRecurrenceIntervalChange,
}: CrmTaskCreateSchedulePanelProps) {
  return (
    <>
      <div className="flex gap-3">
        <input
          type="date"
          value={dueAt}
          onChange={(event) => onDueAtChange(event.target.value)}
          className="flex-1 rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
        />
        <select
          value={priority}
          onChange={(event) => onPriorityChange(event.target.value)}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
        >
          {TASK_PRIORITY_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-3">
        <select
          value={recurrenceFrequency}
          onChange={(event) => onRecurrenceFrequencyChange(event.target.value)}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)]"
          aria-label="Repeat task"
        >
          {TASK_RECURRENCE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <input
          type="number"
          min="1"
          max="365"
          value={recurrenceInterval}
          onChange={(event) => onRecurrenceIntervalChange(event.target.value)}
          disabled={recurrenceFrequency === "none"}
          aria-label="Repeat interval"
          className="w-24 rounded-md border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2 text-sm text-[var(--color-foreground)] disabled:opacity-50"
        />
      </div>
    </>
  );
}
